import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { CreditCard, Landmark, ShieldOff } from 'lucide-react'
import type { CaseDetail } from '../types'

/**
 * AffectedAssetsCard — surfaces the concrete assets a case touches:
 *
 *   - Primary account (NUBAN + bank + account name) when the
 *     anchoring transaction has account-rail context.
 *   - Cards involved: any masked PANs the platform observed on
 *     terminal-context transactions linked to the case. Cards
 *     surface only when the case has a terminal_context block
 *     (POS-anchored cases); other channels don't currently carry
 *     card detail on the case wire payload.
 *
 * Rendered near the top of the case workspace so the analyst sees
 * "what is at risk" before walking the alert / timeline blocks.
 */
export function AffectedAssetsCard({ c }: { c: CaseDetail }) {
  const account = c.primary_account
  // Pull every distinct masked PAN seen on the terminal context's
  // recent-transactions list. Some POS cases have multiple PANs on
  // the same terminal — listing each helps the analyst spot
  // card-testing patterns at a glance.
  const cards = uniqueCards(c)

  if (!account && cards.length === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <ShieldOff size={14} className="text-status-failed" aria-hidden />
          Affected assets
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 p-4 md:grid-cols-2">
        {account && (
          <div className="rounded border border-border bg-muted/15 p-3">
            <p className="flex items-center gap-1.5 text-[0.625rem] uppercase tracking-wide text-muted-fg">
              <Landmark size={11} aria-hidden /> Account
            </p>
            <p className="mt-1 font-mono text-sm font-semibold">{account.nuban}</p>
            <p className="mt-0.5 text-xs text-fg">
              {account.account_name || (
                <span className="text-muted-fg">(account name unavailable)</span>
              )}
            </p>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-fg">
              {account.bank_short_name ? (
                <Badge variant="default" className="font-mono">
                  {account.bank_short_name}
                </Badge>
              ) : null}
              {account.bank_code ? (
                <span className="font-mono text-[0.625rem]">CBN: {account.bank_code}</span>
              ) : (
                <span className="text-[0.625rem] italic">
                  (bank code unresolved — sender_bank_id missing)
                </span>
              )}
            </div>
          </div>
        )}

        {cards.length > 0 && (
          <div className="rounded border border-border bg-muted/15 p-3">
            <p className="flex items-center gap-1.5 text-[0.625rem] uppercase tracking-wide text-muted-fg">
              <CreditCard size={11} aria-hidden /> Cards involved
            </p>
            <ul className="mt-1 space-y-1">
              {cards.map((card) => (
                <li
                  key={card.pan_masked}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-card px-2 py-1 text-xs"
                >
                  <span className="font-mono">{card.pan_masked}</span>
                  <span className="text-[0.625rem] text-muted-fg">
                    {card.transaction_count}× on this terminal
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[0.625rem] text-muted-fg">
              Masked PANs from this terminal's recent transactions. The platform
              never stores full card numbers; PAN masking is enforced at ingest.
            </p>
          </div>
        )}

        {!account && cards.length === 0 && (
          <p className="col-span-2 rounded border border-dashed border-border p-3 text-center text-xs text-muted-fg">
            No account or card data on the wire payload for this case.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

interface CardRow {
  pan_masked: string
  transaction_count: number
}

function uniqueCards(c: CaseDetail): CardRow[] {
  if (!c.terminal_context) return []
  // Terminal context carries per-terminal transactions in the
  // separate /cases/{id}/terminal/transactions endpoint — the main
  // case payload doesn't embed them. Best-effort: if the wire ever
  // grows a `cards` array on the case, surface it here. For now,
  // POS terminal cases without a hydrated transaction list show
  // an empty cards section (the PosCaseBlock further down on the
  // page renders the full transaction grid with PANs).
  type WireWithCards = typeof c & {
    cards?: Array<{ pan_masked: string; transaction_count?: number }>
  }
  const wire = c as WireWithCards
  if (!wire.cards) return []
  return wire.cards.map((card) => ({
    pan_masked: card.pan_masked,
    transaction_count: card.transaction_count ?? 1,
  }))
}
